import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { PlusIcon, TrashIcon, StarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { homepageService } from '../services/homepage';
import { designerService, Designer } from '../services/designer';
import { FeaturedDesignerEntry } from '../types/homepage';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { useToast } from '../contexts/ToastContext';

const FeaturedDesignersManagement: React.FC = () => {
  const [featuredDesigners, setFeaturedDesigners] = useState<FeaturedDesignerEntry[]>([]);
  const [allDesigners, setAllDesigners] = useState<Designer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dragDisabled, setDragDisabled] = useState(false);
  const { showSuccess, showError } = useToast();
  
  // Add designer section state
  const [showAddSection, setShowAddSection] = useState(false);
  const [selectedDesignerId, setSelectedDesignerId] = useState('');
  const [addingDesigner, setAddingDesigner] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [featuredData, designersData] = await Promise.all([
        homepageService.getFeaturedDesigners(),
        designerService.getDesigners({ showDisabled: false })
      ]);
      
      setFeaturedDesigners(featuredData);
      setAllDesigners(designersData.data);
      setError('');
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
      showError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableDesigners = () => {
    const featuredDesignerIds = new Set(featuredDesigners.map(fd => fd.designerId));
    return allDesigners.filter(designer => !featuredDesignerIds.has(designer.id) && designer.isActive);
  };

  const handleAddDesigner = async () => {
    if (!selectedDesignerId) {
      showError('Please select a designer');
      return;
    }

    setAddingDesigner(true);
    try {
      await homepageService.addFeaturedDesigner({
        designerId: selectedDesignerId,
        isActive: true  // Always active when featured
      });

      await loadData();
      showSuccess('Designer added to featured list');
      
      // Reset add section
      setShowAddSection(false);
      setSelectedDesignerId('');
    } catch (err: any) {
      console.error('Failed to add featured designer:', err);
      showError(err.response?.data?.error || 'Failed to add featured designer');
    } finally {
      setAddingDesigner(false);
    }
  };


  const handleRemove = async (featuredDesigner: FeaturedDesignerEntry) => {
    if (!window.confirm(`Are you sure you want to remove "${featuredDesigner.designer.name}" from the featured list?`)) {
      return;
    }

    try {
      await homepageService.removeFeaturedDesigner(featuredDesigner.id);
      await loadData();
      showSuccess('Designer removed from featured list');
    } catch (err: any) {
      console.error('Failed to remove designer:', err);
      showError('Failed to remove designer');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || dragDisabled) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Optimistically update the UI
    const updatedDesigners = Array.from(featuredDesigners);
    const [reorderedItem] = updatedDesigners.splice(sourceIndex, 1);
    updatedDesigners.splice(destinationIndex, 0, reorderedItem);

    // Update display order
    const designersWithOrder = updatedDesigners.map((designer, index) => ({
      ...designer,
      displayOrder: index
    }));

    setFeaturedDesigners(designersWithOrder);
    setDragDisabled(true);

    try {
      // Send update to server
      const orderUpdate = designersWithOrder.map((designer, index) => ({
        id: designer.id,
        displayOrder: index
      }));
      
      await homepageService.updateFeaturedDesignersOrder(orderUpdate);
      showSuccess('Designer order updated successfully');
    } catch (err: any) {
      console.error('Failed to update order:', err);
      showError('Failed to update designer order');
      // Reload to get correct order
      await loadData();
    } finally {
      setDragDisabled(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const availableDesigners = getAvailableDesigners();
  const canAddMore = featuredDesigners.length < 8 && availableDesigners.length > 0;

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Featured Designers</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage which designers appear in the homepage featured section. Maximum 8 designers. Drag designers up or down to reorder.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {!showAddSection && (
            <button
              onClick={() => setShowAddSection(true)}
              disabled={!canAddMore}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Designer
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {featuredDesigners.length >= 8 && (
        <div className="mt-4 rounded-md bg-yellow-50 p-4">
          <div className="text-sm text-yellow-700">
            Maximum of 8 featured designers reached. Remove a designer to add a new one.
          </div>
        </div>
      )}

      {availableDesigners.length === 0 && featuredDesigners.length < 8 && (
        <div className="mt-4 rounded-md bg-blue-50 p-4">
          <div className="text-sm text-blue-700">
            All active designers are already featured. Create new designers to add more to the featured list.
          </div>
        </div>
      )}

      {/* Add Designer Section - Inline instead of modal */}
      {showAddSection && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-indigo-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <StarIcon className="h-5 w-5 text-yellow-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Add Featured Designer</h3>
              </div>
              <button
                onClick={() => {
                  setShowAddSection(false);
                  setSelectedDesignerId('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {availableDesigners.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">
                  No designers available to feature. All active designers are already in the featured list.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Designer Selection Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Designer to Feature
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {availableDesigners.map((designer) => (
                      <label
                        key={designer.id}
                        className={`relative flex cursor-pointer rounded-lg border p-3 focus:outline-none transition-all hover:shadow-md ${
                          selectedDesignerId === designer.id
                            ? 'border-indigo-600 ring-2 ring-indigo-600 bg-indigo-50 shadow-md'
                            : 'border-gray-300 hover:border-gray-400 bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name="designer"
                          value={designer.id}
                          checked={selectedDesignerId === designer.id}
                          onChange={(e) => setSelectedDesignerId(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex flex-col items-center space-y-2 w-full">
                          {/* Designer Logo */}
                          <div className="flex-shrink-0">
                            {(designer.logoUrl || designer.logoPath) ? (
                              <AuthenticatedImage
                                src={designer.logoUrl || `${process.env.REACT_APP_API_URL}/uploads/${designer.logoPath}`}
                                alt={`${designer.name} logo`}
                                className="h-16 w-32 rounded object-contain border border-gray-200"
                                fallback={
                                  <div className="h-16 w-32 rounded bg-gray-200 flex items-center justify-center border border-gray-200">
                                    <span className="text-sm font-medium text-gray-500">
                                      {designer.name.charAt(0)}
                                    </span>
                                  </div>
                                }
                              />
                            ) : (
                              <div className="h-16 w-32 rounded bg-gray-200 flex items-center justify-center border border-gray-200">
                                <span className="text-sm font-medium text-gray-500">
                                  {designer.name.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Designer Info */}
                          <div className="text-center">
                            <div className="text-sm font-medium text-gray-900">
                              {designer.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {designer.dressCount ? `${designer.dressCount} dresses` : 'No dresses'}
                            </div>
                          </div>

                          {/* Selection Indicator */}
                          {selectedDesignerId === designer.id && (
                            <div className="absolute top-2 right-2">
                              <div className="h-5 w-5 rounded-full bg-indigo-600 flex items-center justify-center">
                                <div className="h-2.5 w-2.5 rounded-full bg-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Selected Designer Details */}
                {selectedDesignerId && (
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-start space-x-4">
                      <StarIconSolid className="h-5 w-5 text-yellow-500 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {allDesigners.find(d => d.id === selectedDesignerId)?.name}
                        </h4>
                        {allDesigners.find(d => d.id === selectedDesignerId)?.description && (
                          <p className="text-xs text-gray-600 mt-1">
                            {allDesigners.find(d => d.id === selectedDesignerId)?.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}


                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-2 border-t">
                  <button
                    onClick={() => {
                      setShowAddSection(false);
                      setSelectedDesignerId('');
                    }}
                    disabled={addingDesigner}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddDesigner}
                    disabled={addingDesigner || !selectedDesignerId}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {addingDesigner ? 'Adding...' : 'Add to Featured'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6">
        {featuredDesigners.length === 0 ? (
          <div className="text-center py-12">
            <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No featured designers</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding designers to the featured list.
            </p>
            {availableDesigners.length > 0 && !showAddSection && (
              <div className="mt-6">
                <button
                  onClick={() => setShowAddSection(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Designer
                </button>
              </div>
            )}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable 
              droppableId="featured-designers" 
              isDropDisabled={dragDisabled}
              direction="vertical"
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg p-2 transition-colors duration-200' : ''}`}
                >
                  {featuredDesigners.map((featuredDesigner, index) => (
                    <Draggable 
                      key={featuredDesigner.id} 
                      draggableId={featuredDesigner.id} 
                      index={index}
                      isDragDisabled={dragDisabled}
                    >
                      {(provided, snapshot) => {
                        // Extract only vertical transform, ignore horizontal
                        const getStyle = (style: any, snapshot: DraggableStateSnapshot) => {
                          if (!snapshot.isDragging) {
                            return style;
                          }
                          
                          // Parse the transform to extract only Y translation
                          const transform = style?.transform;
                          if (transform) {
                            const match = transform.match(/translate\(.*?,\s*(-?\d+(?:\.\d+)?px)\)/);
                            if (match) {
                              // Keep only vertical translation, reset horizontal to 0
                              return {
                                ...style,
                                transform: `translate(0px, ${match[1]})`,
                              };
                            }
                          }
                          
                          return style;
                        };
                        
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={getStyle(provided.draggableProps.style, snapshot)}
                            className={`bg-white rounded-lg shadow-sm border transition-all duration-200 ${
                              snapshot.isDragging 
                                ? 'shadow-xl border-indigo-400 opacity-95 scale-[1.02] z-50' 
                                : 'border-gray-200 hover:shadow-md hover:border-gray-300'
                            } ${dragDisabled ? 'cursor-not-allowed' : ''} ${
                              snapshot.combineWith ? 'opacity-0' : ''
                            }`}
                          >
                          <div className="p-6">
                            <div className="flex items-start space-x-4">
                              {/* Drag Handle */}
                              <div 
                                {...provided.dragHandleProps}
                                className={`flex-shrink-0 mt-2 ${dragDisabled ? 'cursor-not-allowed opacity-50' : snapshot.isDragging ? 'cursor-grabbing' : 'cursor-grab hover:bg-gray-100 rounded p-1 transition-colors'}`}
                                title="Drag to reorder"
                              >
                                <div className="w-6 h-6 flex flex-col items-center justify-center space-y-1">
                                  <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
                                  <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
                                  <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
                                </div>
                              </div>

                              {/* Designer Logo */}
                              <div className="flex-shrink-0">
                                {(featuredDesigner.designer.logoUrl || featuredDesigner.designer.logoPath) ? (
                                  <AuthenticatedImage
                                    src={featuredDesigner.designer.logoUrl || `${process.env.REACT_APP_API_URL}/uploads/${featuredDesigner.designer.logoPath}`}
                                    alt={`${featuredDesigner.designer.name} logo`}
                                    className="h-16 w-32 rounded-lg object-contain border border-gray-200"
                                    fallback={
                                      <div className="h-16 w-32 rounded-lg bg-gray-200 flex items-center justify-center border border-gray-200">
                                        <span className="text-sm font-medium text-gray-500">
                                          {featuredDesigner.designer.name.charAt(0)}
                                        </span>
                                      </div>
                                    }
                                  />
                                ) : (
                                  <div className="h-16 w-32 rounded-lg bg-gray-200 flex items-center justify-center border border-gray-200">
                                    <span className="text-sm font-medium text-gray-500">
                                      {featuredDesigner.designer.name.charAt(0)}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Designer Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center">
                                  <StarIconSolid className="h-4 w-4 text-yellow-500 mr-2" />
                                  <h3 className="text-lg font-medium text-gray-900">
                                    {featuredDesigner.designer.name}
                                  </h3>
                                </div>
                                {featuredDesigner.designer.description && (
                                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                                    {featuredDesigner.designer.description}
                                  </p>
                                )}
                                <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100">
                                    Position: {index + 1} of {featuredDesigners.length}
                                  </span>
                                  {!featuredDesigner.designer.isActive && (
                                    <span className="text-red-600 font-medium">Designer is inactive</span>
                                  )}
                                </div>
                              </div>

                              {/* Remove Button */}
                              <div className="flex-shrink-0">
                                <button
                                  onClick={() => handleRemove(featuredDesigner)}
                                  className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  <TrashIcon className="h-3 w-3 mr-1" />
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default FeaturedDesignersManagement;